import { CloseIcon } from 'assets/icons';
import { Footer } from 'components/details-pages/Footer';
import { Header } from 'components/details-pages/Header';
import React from 'react';
import { useNavigate } from 'react-router';
import { AppRoutes } from 'routes/app-routes';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="relative flex flex-col min-h-screen">
      <Header />
      <div className="flex flex-col content-between w-full max-w-[969px] mx-auto ">
        <div className="py-6 px-4 sm:px-10">
          <div className="flex justify-between items-center border-b py-8 border-silver-400 dark:border-white-400/20">
            <p className="text-gray-500 text-[28px] dark:text-silver-400 font-semibold leading-[17px]">
              Terms of Service
            </p>
            <CloseIcon
              className="cursor-pointer dark:text-white-400 text-silver-800"
              onClick={() => navigate(AppRoutes.Roster)}
            />
          </div>
          <div className="mt-5 text-lg dark:text-white-400 text-silver-800 flex flex-col gap-5 leading-6">
            <p>
              <strong>End User License Agreement</strong>
            </p>
            <p>
              This End User License Agreement (“Agreement”) is a binding agreement between you (“End User” or “you”) and
              Nascend, LLC (“Company”). This Agreement governs your use of NASCEND ASSESS for iPhone, (including all
              related documentation, the “Application”). The Application is licensed, not sold, to you.
            </p>
            <p>
              BY CLICKING THE “AGREE” BUTTON, YOU (A) ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTAND THIS AGREEMENT; (B)
              REPRESENT THAT YOU ARE 18 YEARS OF AGE OR OLDER; AND (C) ACCEPT THIS AGREEMENT AND AGREE THAT YOU ARE
              LEGALLY BOUND BY ITS TERMS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT DOWNLOAD, INSTALL OR USE THE
              APPLICATION.
            </p>
            <ol start={1} className="list-decimal flex flex-col gap-5 leading-6">
              <li
                className="leading-6
              "
              >
                <u>License Grant</u>. Subject to the terms of this Agreement, Company grants you a limited,
                non-exclusive, and nontransferable license to download, install, and use the Application for your
                personal, non-commercial use on a single iPhone owned or otherwise controlled by you (“Mobile Device”)
                strictly in accordance with the Application’s documentation.&nbsp; You will pay Company’s license and
                subscription fees as displayed upon download and updated from time to time (“Fee”) and you will comply
                with all applicable laws in your use of the Application.
              </li>
              <li>
                <u>License Restrictions</u>. Licensee shall not:
                <ol className="list-decimal pl-8 flex flex-col gap-5">
                  <li>(a) copy the Application, except as expressly permitted by this license;</li>
                  <li>
                    (b) modify, translate, adapt, or otherwise create derivative works or improvements, whether or not
                    patentable, of the Application;
                  </li>
                  <li>
                    (c) reverse engineer, disassemble, decompile, decode, or otherwise attempt to derive or gain access
                    to the source code of the Application or any part thereof;
                  </li>
                  <li>
                    (d) remove, delete, alter, or obscure any trademarks or any copyright, trademark, patent, or other
                    intellectual property or proprietary rights notices from the Application, including any copy
                    thereof;
                  </li>
                  <li>
                    (e) rent, lease, lend, sell, sublicense, assign, distribute, publish, transfer, or otherwise make
                    available the Application, or any features or functionality of the Application, to any third party
                    for any reason, including by making the Application available on a network where it is capable of
                    being accessed by more than one device at any time;
                  </li>
                  <li>
                    (f) remove, disable, circumvent, or otherwise create or implement any workaround to any copy
                    protection, rights management, or security features in or protecting the Application; or
                  </li>
                  <li>
                    (g) use the Application in, or in association with, the design, construction, maintenance, or
                    operation of any hazardous environments or systems, including any power generation systems; aircraft
                    navigation or communication systems, air traffic control systems, or any other transport management
                    systems; safety-critical applications, including life-support systems, vehicle operation
                    applications or any police, fire, or other safety response systems; and military or aerospace
                    applications, weapons systems, or environments.
                  </li>
                  <li>
                    <u>Reservation of Rights</u>. You acknowledge and agree that the Application is provided under
                    license, and not sold, to you. You do not acquire any ownership interest in the Application under
                    this Agreement, or any other rights thereto other than to use the Application in accordance with the
                    license granted, and subject to all terms, conditions, and restrictions, under this Agreement.
                    Company reserves and shall retain its entire right, title, and interest in and to the Application,
                    including all copyrights, trademarks, and other intellectual property rights therein or relating
                    thereto, except as expressly granted to you in this Agreement.
                  </li>
                  <li>
                    <u>Collection and Use of Your Information</u>. You acknowledge that when you download, install, or
                    use the Application, Company may use automatic means (including, for example, cookies and web
                    beacons) to collect information about your Mobile Device and about your use of the Application. All
                    information we collect through or in connection with this Application is subject to our Privacy
                    Policy [<a href="https://nascend.com/privacypolicy">http://nascend.com/privacypolicy</a>], which is
                    incorporated by reference. By downloading, installing, using, and providing information to or
                    through this Application, you consent to all actions taken by us with respect to your information in
                    compliance with the Privacy Policy.&nbsp; The Application does not collect and you will not provide
                    any personally identifiable information or other information that you do not have the right to
                    provide or transmit under applicable law, including without limitation HIPAA.
                  </li>
                  <li>
                    <u>Medical</u>.&nbsp; The Application does not provide medical advice, diagnosis, or treatment. You
                    acknowledge and agree that the Application merely transfers general information related to patients
                    and the provision of healthcare to patients by independent third party healthcare providers
                    including yourself, physicians, physician assistants, nurses, nurse pratitioners, paramedics,
                    emergency care responders, other physician extenders, healthcare systems, healthcare facilities, or
                    other providers of healthcare services (collectively, “Healthcare Provider(s)”). You acknowledge and
                    agree that the Healthcare Providers are solely responsible for and will have complete authority,
                    responsibility, supervision, and control over the provision of all medical services, advice,
                    instructions, treatment decisions, and other professional health care services performed, and that
                    all diagnoses, treatments, procedures, and other professional health care services will be provided
                    and performed exclusively by or under the supervision of Healthcare Providers as they, in their sole
                    discretion, deem appropriate. You further acknowledge and agree that Company does not provide or
                    endorse any medical advice on or through the Application and no information obtained through the
                    Application can be so construed or used. Company will have and exercise absolutely no control,
                    authority, or supervision over the provision of any medical services or other professional health
                    care services. You acknowledge and agree that you are solely responsible for all medical services,
                    advice, instructions, diagnoses, treatments, procedures, and other services you provide in
                    connection with using the Application or otherwise.
                  </li>
                  <li>
                    <u>Content and Services</u>. The Application may provide you with access to Company’s website
                    located at [https://nascend.com] (the “Website”) and products and services accessible thereon, and
                    certain features, functionality, and content accessible on or through the Application may be hosted
                    on the Website (collectively, “Content and Services”). Your access to and use of such Content and
                    Services are governed by Website’s Terms of Use and Privacy Policy located at [
                    <a href="https://nascend.com/termsofservice">http://nascend.com/termsofservice</a>] and [
                    <a href="https://nascend.com/privacypolicy">http://nascend.com/privacypolicy</a>], which are
                    incorporated herein by this reference. Your access to and use of such Content and Services may
                    require you to acknowledge your acceptance of such Terms of Use and Privacy Policy and/or to
                    register with the Website, and your failure to do so may restrict you from accessing or using
                    certain of the Application’s features and functionality. Any violation of such Terms of Use will
                    also be deemed a violation of this Agreement.
                  </li>
                  <li>
                    <u>Geographic Restrictions</u>. The Content and Services are based in the Commonwealth of Kentucky
                    in the United States and provided for access and use only by persons located in the United States.
                    If you access the Content and Services from outside the United States, you are responsible for
                    compliance with local laws.
                  </li>
                  <li>
                    <u>Updates</u>. Company may from time to time in its sole discretion develop and provide Application
                    updates, which may include upgrades, bug fixes, patches, other error corrections, and/or new
                    features (collectively, including related documentation, “Updates”). Updates may also modify or
                    delete in their entirety certain features and functionality. You agree that Company has no
                    obligation to provide any Updates or to continue to provide or enable any particular features or
                    functionality. Based on your Mobile Device settings, when your Mobile Device is connected to the
                    internet either the Application will automatically download and install all available Updates or you
                    may receive notice of or be prompted to download and install available Updates. You shall promptly
                    download and install all Updates and acknowledge and agree that the Application or portions thereof
                    may not properly operate should you fail to do so. You further agree that all Updates will be deemed
                    part of the Application and be subject to all terms and conditions of this Agreement.
                  </li>
                  <li>
                    &nbsp; <u>Third-Party Materials</u>. The Application may display, include, or make available
                    third-party content (including data, information, applications, and other products, services, and/or
                    materials) or provide links to third-party websites or services, including through third-party
                    advertising (“Third-Party Materials”). You acknowledge and agree that Company is not responsible for
                    Third-Party Materials, including their accuracy, completeness, timeliness, validity, copyright
                    compliance, legality, decency, quality, or any other aspect thereof. Company does not assume and
                    will not have any liability or responsibility to you or any other person or entity for any
                    Third-Party Materials. Third-Party Materials and links thereto are provided solely as a convenience
                    to you, and you access and use them entirely at your own risk and subject to such third parties’
                    terms and conditions.
                  </li>
                  <li>
                    <u>Term and Termination</u>.
                  </li>
                  <li>
                    (a) The term of the Agreement commences when you acknowledge your acceptance by clicking “Agree” and
                    will continue in effect until terminated by you or Company as set forth in this Section 10.
                  </li>
                  <li>
                    (b) You may terminate this Agreement by deleting the Application and all copies thereof from your
                    Mobile Device or by ceasing to pay Fees.
                  </li>
                  <li>
                    (c) Company may terminate this Agreement at any time in its sole discretion without notice if it
                    ceases to support or offer the Application or if you fail to pay any Fees. In addition, this
                    Agreement will terminate immediately and automatically without any notice if you violate any of the
                    terms and conditions of this Agreement.
                  </li>
                  <li>
                    (d) Upon termination, all rights granted to you under this Agreement will also terminate, you must
                    cease all use of the Application and delete all copies of the Application from your Mobile Device
                    and account.
                  </li>
                  <li>
                    (e) Termination will not limit any of Company’s rights or remedies at law or in equity.&nbsp; There
                    will be no refund of any Fees upon termination.
                  </li>
                  <li>
                    <u>Disclaimer of Warranties</u>. THE APPLICATION IS PROVIDED TO LICENSEE “AS IS” AND WITH ALL FAULTS
                    AND DEFECTS WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW,
                    COMPANY, ON ITS OWN BEHALF AND ON BEHALF OF ITS AFFILIATES AND ITS AND THEIR RESPECTIVE LICENSORS
                    AND SERVICE PROVIDERS, EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR
                    OTHERWISE, WITH RESPECT TO THE APPLICATION, INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY,
                    FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, AND WARRANTIES THAT MAY ARISE OUT OF
                    COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE, OR TRADE PRACTICE. WITHOUT LIMITATION TO THE
                    FOREGOING, COMPANY PROVIDES NO WARRANTY OR UNDERTAKING, AND MAKES NO REPRESENTATION OF ANY KIND THAT
                    THE APPLICATION WILL MEET YOUR REQUIREMENTS, ACHIEVE ANY INTENDED RESULTS, BE COMPATIBLE, OR WORK
                    WITH ANY OTHER SOFTWARE, APPLICATIONS, SYSTEMS, OR SERVICES, OPERATE WITHOUT INTERRUPTION, MEET ANY
                    PERFORMANCE OR RELIABILITY STANDARDS, OR BE ERROR-FREE, OR THAT ANY ERRORS OR DEFECTS CAN OR WILL BE
                    CORRECTED. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF OR LIMITATIONS ON IMPLIED WARRANTIES OR
                    THE LIMITATIONS ON THE APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO SOME OR ALL OF THE ABOVE
                    EXCLUSIONS AND LIMITATIONS MAY NOT APPLY TO YOU.
                  </li>
                  <li>
                    <u>Limitation of Liability</u>. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL
                    COMPANY OR ITS AFFILIATES, OR ANY OF ITS OR THEIR RESPECTIVE LICENSORS OR SERVICE PROVIDERS, HAVE
                    ANY LIABILITY ARISING FROM OR RELATED TO YOUR USE OF OR INABILITY TO USE THE APPLICATION OR THE
                    CONTENT AND SERVICES FOR:
                  </li>
                  <li>
                    (a) PERSONAL INJURY, PROPERTY DAMAGE, LOST PROFITS, COST OF SUBSTITUTE GOODS OR SERVICES, LOSS OF
                    DATA, LOSS OF GOODWILL, BUSINESS INTERRUPTION, COMPUTER FAILURE OR MALFUNCTION, OR ANY OTHER
                    CONSEQUENTIAL, INCIDENTAL, INDIRECT, EXEMPLARY, SPECIAL, OR PUNITIVE DAMAGES.
                  </li>
                  <li>
                    (b) DIRECT DAMAGES IN AMOUNTS THAT IN THE AGGREGATE EXCEED THE AMOUNT ACTUALLY PAID BY YOU FOR THE
                    APPLICATION.
                  </li>
                </ol>
              </li>
            </ol>
            <p>
              THE FOREGOING LIMITATIONS WILL APPLY WHETHER SUCH DAMAGES ARISE OUT OF BREACH OF CONTRACT, TORT (INCLUDING
              NEGLIGENCE), OR OTHERWISE AND REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE OR COMPANY WAS ADVISED
              OF THE POSSIBILITY OF SUCH DAMAGES. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS OF LIABILITY SO
              SOME OR ALL OF THE ABOVE LIMITATIONS OF LIABILITY MAY NOT APPLY TO YOU.
            </p>
            <p></p>
            <ol className="list-decimal flex flex-col gap-5" start={13}>
              <li>
                <u>Indemnification</u>. You agree to indemnify, defend, and hold harmless Company and its officers,
                directors, employees, agents, affiliates, successors, and assigns from and against any and all losses,
                damages, liabilities, deficiencies, claims, actions, judgments, settlements, interest, awards,
                penalties, fines, costs, or expenses of whatever kind, including [reasonable] attorneys’ fees, arising
                from or relating to your use or misuse of the Application or your breach of this Agreement, including
                but not limited to the content you submit or make available through this Application.
              </li>
              <li>
                <u>Export Regulation</u>. The Application may be subject to US export control laws, including the US
                Export Administration Act and its associated regulations. You shall not, directly or indirectly, export,
                re-export, or release the Application to, or make the Application accessible from, any jurisdiction or
                country to which export, re-export, or release is prohibited by law, rule, or regulation. You shall
                comply with all applicable federal laws, regulations, and rules, and complete all required undertakings
                (including obtaining any necessary export license or other governmental approval), prior to exporting,
                re-exporting, releasing, or otherwise making the Application available outside the US.
              </li>
              <li>
                <u>US Government Rights</u>. The Application is commercial computer software, as such term is defined in
                48 C.F.R. §2.101. Accordingly, if you are an agency of the US Government or any contractor therefor, you
                receive only those rights with respect to the Application as are granted to all other end users under
                license, in accordance with (a) 48 C.F.R. §227.7201 through 48 C.F.R. §227.7204, with respect to the
                Department of Defense and their contractors, or (b) 48 C.F.R. §12.212, with respect to all other US
                Government licensees and their contractors.
              </li>
              <li>
                <u>Severability</u>. If any provision of this Agreement is illegal or unenforceable under applicable
                law, the remainder of the provision will be amended to achieve as closely as possible the effect of the
                original term and all other provisions of this Agreement will continue in full force and effect[;
                provided, however, that if any fundamental term or provision of this Agreement is invalid, illegal, or
                unenforceable, the remainder of this Agreement shall be unenforceable.
              </li>
              <li>
                <u>Governing Law</u>. This Agreement is governed by and construed in accordance with the internal laws
                of the Commonwealth of Kentucky without giving effect to any choice or conflict of law provision or
                rule. Any legal suit, action, or proceeding arising out of or related to this Agreement or the
                Application shall be instituted exclusively in the federal courts of the United States or the courts of
                the Commonwealth of Kentucky in each case located in Louisville and Jefferson County. You waive any and
                all objections to the exercise of jurisdiction over you by such courts and to venue in such courts.
              </li>
              <li>
                <u>Limitation of Time to File Claims</u>. ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR
                RELATING TO THIS AGREEMENT OR THE APPLICATION MUST BE COMMENCED WITHIN ONE (1) YEAR AFTER THE CAUSE OF
                ACTION ACCRUES OTHERWISE SUCH CAUSE OF ACTION OR CLAIM IS PERMANENTLY BARRED.
              </li>
              <li>
                <u>Entire Agreement</u>. This Agreement and our Privacy Policy constitute the entire agreement between
                you and Company with respect to the Application and supersede all prior or contemporaneous
                understandings and agreements, whether written or oral, with respect to the Application.
              </li>
              <li>
                <u>Waiver</u>. No failure to exercise, and no delay in exercising, on the part of either party, any
                right or any power hereunder shall operate as a waiver thereof, nor shall any single or partial exercise
                of any right or power hereunder preclude further exercise of that or any other right hereunder. In the
                event of a conflict between this Agreement and any applicable purchase or other terms, the terms of this
                Agreement shall govern.
              </li>
            </ol>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default TermsOfService;
